/**
 * T1258 · Devtool installer · single SDKProvider integration point.
 *
 * Composes every devtool channel installer into one entry point that
 * SDKProvider invokes once from a `useEffect`. Returns a single
 * dispose function the provider calls on unmount.
 *
 * **Production DCE.** When `DEVTOOL_ENABLED` is false, returns a
 * no-op closure WITHOUT subscribing to anything. Zero subscribers on
 * QueryCache; zero memory cost.
 *
 * **Double-install guard.** A module-level `WeakSet<QueryClient>`
 * tracks already-installed clients. Second `installDevtool(qc)` with
 * the same instance logs a `console.warn` and returns a no-op.
 *
 * **Per-QueryClient isolation.** Separate QueryClient instances
 * (Next.js per-request server cache) get independent installs.
 */

import {
  type QueryClientLike as CacheMissQc,
  installCacheMissChannel,
} from './channels/cache-miss.js';
import {
  type QueryClientLike as CacheWriteQc,
  installCacheWriteChannel,
} from './channels/cache-write.js';
import { isDevtoolEnabled } from './events.js';

/**
 * Structural shape — every concrete QueryClient implements both. We
 * intersect so the install function accepts the canonical instance.
 */
export type DevtoolQueryClient = CacheMissQc & CacheWriteQc;

const installed = new WeakSet<DevtoolQueryClient>();

/**
 * Install all devtool channels against `qc`. Returns a dispose closure
 * that detaches every subscriber and clears the install guard.
 *
 * Calling twice with the same `qc` logs a warning and returns a no-op
 * (the original installation is left intact).
 */
export function installDevtool(qc: DevtoolQueryClient): () => void {
  if (!isDevtoolEnabled()) {
    return (): void => {};
  }
  if (installed.has(qc)) {
    // Avoid the dual-subscriber hazard — log and return no-op.
    console.warn(
      '[morfi-sdk:devtool] installDevtool called twice with the same QueryClient — second call is a no-op.',
    );
    return (): void => {};
  }
  installed.add(qc);

  const offMiss = installCacheMissChannel(qc);
  const offWrite = installCacheWriteChannel(qc);

  let disposed = false;
  return (): void => {
    if (disposed) return;
    disposed = true;
    offMiss();
    offWrite();
    installed.delete(qc);
  };
}

/** Test-only inspector — exposes the install guard. */
export function __isInstalledForTests(qc: DevtoolQueryClient): boolean {
  return installed.has(qc);
}
